import React, { useState, useEffect, useRef } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Alert } from 'react-bootstrap';
import { formatCurrency } from '../../utils/currency';
import { ensureFreshPaymentIntent, createOrderReservation, releaseOrderReservation, saveShippingQuote, validateShippingQuote } from '../../services/api';

const PaymentForm = ({ orderTotal, onSuccess, onError, clientSecret, orderId, quoteData, billingDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);
  const isProcessingRef = useRef(false);
  const didRetryRef = useRef(false); // Guard against retry loops

  // Save quote when component mounts or quote data changes
  useEffect(() => {
    if (quoteData && orderId) {
      const saveQuote = async () => {
        try {
          // Just send the quote data as-is, backend will extract what it needs
          const quotePayload = {
            quote_payload: quoteData,
            // Try to extract common fields if they exist
            carrier: quoteData.carrier || 'FedEx',
            service: quoteData.service_name || quoteData.service || 'Priority Overnight'
          };
          
          // Try to find amount_cents if available
          if (quoteData.amount_cents) {
            quotePayload.amount_cents = quoteData.amount_cents;
          } else if (quoteData.amount) {
            quotePayload.amount_cents = Math.round(quoteData.amount * 100);
          } else if (quoteData.total) {
            quotePayload.amount_cents = Math.round(quoteData.total * 100);
          }
          // If no amount found, backend will try to derive it
          
          console.log('[PaymentForm] Saving quote on mount:', quotePayload);
          await saveShippingQuote(orderId, quotePayload);
          console.log('[PaymentForm] Quote saved successfully on mount');
        } catch (error) {
          console.error('[PaymentForm] Failed to save quote on mount:', error);
        }
      };
      
      saveQuote();
    }
  }, [quoteData, orderId]);

  // Helper function to validate shipping quote (no auto-generation)
  const validateCurrentQuote = async (orderId) => {
    console.log('[PaymentForm] Validating current shipping quote for order:', orderId);
    
    try {
      const validation = await validateShippingQuote(orderId);
      console.log('[PaymentForm] Quote validation result:', validation);
      
      if (validation.status === 'valid') {
        console.log('[PaymentForm] Quote is valid, can proceed with payment');
        return true;
      } else {
        console.log('[PaymentForm] Quote validation failed:', validation.error);
        return false;
      }
    } catch (validationError) {
      console.error('[PaymentForm] Quote validation error:', validationError);
      return false;
    }
  };

  // Listen for PaymentElement changes
  useEffect(() => {
    if (!elements) return;

    const paymentElement = elements.getElement('payment');
    if (!paymentElement) return;

    const handleChange = (event) => {
      console.log('[PaymentForm] elements.change', {
        complete: event.complete,
        empty: event.empty,
        error: event.error
      });
      setIsCardComplete(event.complete);
      if (event.error) {
        setError(event.error.message);
      } else {
        setError(null);
      }
    };

    const handleReady = () => {
      console.log('[PaymentForm] elements.ready');
      setIsElementReady(true);
    };

    paymentElement.on('ready', handleReady);
    paymentElement.on('change', handleChange);

    return () => {
      paymentElement.off('ready', handleReady);
      paymentElement.off('change', handleChange);
    };
  }, [elements]);

  // Button is enabled when:
  // - Stripe and Elements are loaded
  // - Elements are ready
  // - Card details are complete
  // - We have a client secret
  // - Not currently processing
  const isButtonDisabled = !stripe || !elements || !isElementReady || !isCardComplete || !clientSecret || isProcessing;
  
  // Log button state changes only when they occur
  React.useEffect(() => {
    console.log('[PaymentForm] button.enabled changed:', {
      stripeReady: !!stripe,
      elementsReady: !!elements && isElementReady,
      cardComplete: isCardComplete,
      clientSecretPresent: !!clientSecret,
      isProcessing,
      buttonDisabled: isButtonDisabled
    });
  }, [stripe, elements, isElementReady, isCardComplete, clientSecret, isProcessing, isButtonDisabled]);

  // ONE condition for "we will supply billing ourselves". The two sites
  // below used to disagree — `fields` hid Stripe's billing inputs whenever
  // the prop merely existed, while `payment_method_data` only attached
  // when a NAME was present. A nameless order would therefore hide the
  // fields AND send nothing. Can't happen today (the address form requires
  // first+last name) but the shape doesn't belong in this file.
  const willSupplyBilling = !!(billingDetails && billingDetails.name);

  useEffect(() => {
    // console.warn survives the production terser strip (console.log does
    // not), so this is the DEPLOYED-build signal for the Feb-2026 shape:
    // a charge landing with null identity fields, which degrades Radar/AVS
    // and produced months of invisible declines. Covers BOTH ways we can
    // end up not supplying billing — a null prop (reachable: refresh after
    // the checkout address state is gone) and a nameless one.
    // Precise about the fallback: for a card, `fields: {address:'auto'}`
    // collects the ADDRESS only. Stripe does NOT collect cardholder name,
    // email or phone, so the charge really does go out with name=null.
    if (!willSupplyBilling) {
      console.warn(
        billingDetails
          ? '[PaymentForm] billingDetails present but name is empty — Stripe collects address only; this charge will carry name=null'
          : '[PaymentForm] billingDetails is NULL at mount — Stripe collects address only; this charge will carry name=null (checkout address state did not reach this page)'
      );
    }
  }, [billingDetails, willSupplyBilling]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    console.log('[PaymentForm] Pay button clicked - starting new payment flow with animal reservation');

    // Guard against double submission using ref for immediate check
    if (isProcessingRef.current || isProcessing || !stripe || !elements || !clientSecret) {
      console.log('[PaymentForm] GATING: Payment blocked - not ready', {
        isProcessing: isProcessingRef.current || isProcessing,
        stripeReady: !!stripe,
        elementsReady: !!elements,
        clientSecretPresent: !!clientSecret
      });
      return;
    }

    console.log('[PaymentForm] GATING: All conditions met, proceeding with payment flow');
    isProcessingRef.current = true;
    setIsProcessing(true);
    setError(null);

    // Generate idempotency key for this payment attempt
    const idempotencyKey = `${orderId}-${Date.now()}`;
    let animalsReserved = false;

    try {
      // Step 1: Reserve animals first (with idempotency)
      console.log('[PaymentForm] Step 1: Reserving animals for order:', orderId);
      
      try {
        const reservationResponse = await createOrderReservation(orderId, {
          headers: {
            'x-idempotency-key': idempotencyKey
          }
        });
        
        console.log('[PaymentForm] Animal reservation successful:', reservationResponse);
        animalsReserved = true;
        
      } catch (reserveError) {
        console.error('[PaymentForm] Animal reservation failed:', reserveError);
        
        if (reserveError.response?.status === 409) {
          // Insufficient stock - show specific error
          const conflictData = reserveError.response.data;
          if (conflictData.unavailable && conflictData.unavailable.length > 0) {
            const itemsList = conflictData.unavailable
              .map(item => `${item.name}: needed ${item.needed}, available ${item.available}`)
              .join('; ');
            setError(`Some items are no longer available: ${itemsList}`);
          } else {
            setError('Some items are no longer available. Please return to your cart.');
          }
        } else {
          setError(`Failed to reserve animals: ${reserveError.message || 'Unknown error'}`);
        }
        
        // Re-enable button on reservation failure
        isProcessingRef.current = false;
        setIsProcessing(false);
        return;
      }

      // Step 2: Validate payment element (submit to Stripe)
      console.log('[PaymentForm] Step 2: Submitting payment element to Stripe');
      const { error: submitError } = await elements.submit();
      
      if (submitError) {
        console.log('[PaymentForm] elements.submit.fail', {
          code: submitError.code,
          type: submitError.type,
          message: submitError.message
        });
        setError(submitError.message || 'Failed to submit payment information');
        
        // Release reserved animals since payment failed
        console.log('[PaymentForm] Releasing reserved animals due to payment validation failure');
        try {
          await releaseOrderReservation(orderId, {
            headers: {
              'x-idempotency-key': idempotencyKey
            }
          });
          console.log('[PaymentForm] Animals released successfully');
        } catch (releaseError) {
          console.error('[PaymentForm] Failed to release animals:', releaseError);
        }
        
        isProcessingRef.current = false;
        setIsProcessing(false);
        return;
      }
      
      console.log('[PaymentForm] Payment element validation successful');

      // Step 3: Save shipping quote - be very tolerant
      console.log('[PaymentForm] Step 3: Saving shipping quote before payment');
      
      try {
        // Try to save with whatever data we have - backend will handle it
        const quotePayload = {};
        
        if (quoteData) {
          quotePayload.quote_payload = quoteData;
          
          // Try to extract fields if available
          if (quoteData.amount_cents) {
            quotePayload.amount_cents = quoteData.amount_cents;
          } else if (quoteData.amount) {
            quotePayload.amount_cents = Math.round(quoteData.amount * 100);
          } else if (quoteData.total) {
            quotePayload.amount_cents = Math.round(quoteData.total * 100);
          }
          
          if (quoteData.carrier) quotePayload.carrier = quoteData.carrier;
          if (quoteData.service || quoteData.service_name) {
            quotePayload.service = quoteData.service || quoteData.service_name;
          }
        }
        
        console.log('[PaymentForm] Calling save-shipping-quote with:', quotePayload);
        const quoteResult = await saveShippingQuote(orderId, quotePayload);
        console.log('[PaymentForm] Quote saved successfully:', quoteResult);
        
      } catch (quoteError) {
        console.error('[PaymentForm] Failed to save shipping quote:', quoteError);
        
        // Only stop if we get a 422 indicating no shipping available
        if (quoteError.response?.status === 422) {
          setError('No shipping quote on order — go back to shipping step.');
          
          // Release animals since we can't proceed
          try {
            await releaseOrderReservation(orderId, {
              headers: { 'x-idempotency-key': idempotencyKey }
            });
          } catch (releaseError) {
            console.warn('[PaymentForm] Failed to release animals:', releaseError);
          }
          
          isProcessingRef.current = false;
          setIsProcessing(false);
          return;
        }
        
        // For any other error, log and continue - maybe quote is already saved
        console.warn('[PaymentForm] Quote save had issues but continuing:', quoteError.message);
      }

      // Step 4: Get fresh payment intent for confirmation
      console.log('[PaymentForm] Step 4: Getting fresh payment intent for order:', orderId);
      let freshPI;
      try {
        freshPI = await ensureFreshPaymentIntent(orderId);
        console.log('[PaymentForm] Fresh PI received:', {
          id: freshPI.id,
          status: freshPI.status,
          created_fresh: freshPI.created_fresh,
          reused_existing: freshPI.reused_existing,
          needs_reconciliation: freshPI.needs_reconciliation
        });
      } catch (piError) {
        console.error('[PaymentForm] Failed to get fresh PI:', piError);
        
        // Handle shipping quote missing error
        if (piError.response?.data?.error === 'SHIPPING_QUOTE_MISSING' || piError.response?.data?.error === 'SHIPPING_QUOTE_STALE') {
          console.log('[PaymentForm] Shipping quote missing - quote validation failed');
          
          const message = 'No shipping quote on order — go back to shipping step.';
          
          console.log(`[PaymentForm] Quote validation failed, showing user message`);
          setError(message);
          
          // DO NOT release animals - keep them reserved
          console.log('[PaymentForm] Keeping animals reserved - user needs to go back to shipping');
          
        } else {
          setError('Failed to initialize payment. Please try again.');
          
          // Only release animals for non-quote errors
          console.log('[PaymentForm] Releasing reserved animals due to PI failure');
          try {
            await releaseOrderReservation(orderId, {
              headers: {
                'x-idempotency-key': idempotencyKey
              }
            });
            console.log('[PaymentForm] Animals released successfully');
          } catch (releaseError) {
            console.error('[PaymentForm] Failed to release animals:', releaseError);
          }
        }
        
        isProcessingRef.current = false;
        setIsProcessing(false);
        return;
      }

      // Check if PI already succeeded or needs reconciliation
      if (freshPI.status === 'succeeded' || freshPI.needs_reconciliation) {
        console.log('[PaymentForm] PI already succeeded/processing - skipping confirmPayment, going to success');
        // PI already succeeded, no need to confirm again
        onSuccess?.(freshPI);
        return;
      }

      // Only confirm if PI needs payment
      if (freshPI.status !== 'requires_payment_method' && freshPI.status !== 'requires_confirmation') {
        console.log('[PaymentForm] Unexpected PI status:', freshPI.status);
        setError('Payment is in an unexpected state. Please refresh and try again.');
        
        // Release reserved animals since payment is in unexpected state
        console.log('[PaymentForm] Releasing reserved animals due to unexpected PI status');
        try {
          await releaseOrderReservation(orderId, {
            headers: {
              'x-idempotency-key': idempotencyKey
            }
          });
          console.log('[PaymentForm] Animals released successfully');
        } catch (releaseError) {
          console.error('[PaymentForm] Failed to release animals:', releaseError);
        }
        
        isProcessingRef.current = false;
        setIsProcessing(false);
        return;
      }

      // Step 5: Confirm payment with Stripe
      // Build billing_details from checkout data so Stripe has real customer info for AVS/Radar
      const confirmParams = {
        return_url: `${window.location.origin}/checkout/complete`,
      };
      if (willSupplyBilling) {
        confirmParams.payment_method_data = {
          billing_details: {
            name: billingDetails.name,
            ...(billingDetails.email && { email: billingDetails.email }),
            ...(billingDetails.phone && { phone: billingDetails.phone }),
            address: {
              ...(billingDetails.address?.line1 && { line1: billingDetails.address.line1 }),
              ...(billingDetails.address?.line2 && { line2: billingDetails.address.line2 }),
              ...(billingDetails.address?.city && { city: billingDetails.address.city }),
              ...(billingDetails.address?.state && { state: billingDetails.address.state }),
              ...(billingDetails.address?.postal_code && { postal_code: billingDetails.address.postal_code }),
              ...(billingDetails.address?.country && { country: billingDetails.address.country }),
            },
          },
        };
      }
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: freshPI.client_secret,
        confirmParams,
        redirect: 'if_required',
      });

      if (confirmError) {
        console.log('[PaymentForm] confirmPayment.fail', {
          code: confirmError.code,
          type: confirmError.type,
          message: confirmError.message
        });
        
        // Special handling for already succeeded payment intent
        if (confirmError.code === 'payment_intent_unexpected_state' && 
            confirmError.payment_intent?.status === 'succeeded') {
          console.log('[PaymentForm] PI already succeeded - treating as success');
          console.log('[PaymentForm] Succeeded PI details:', confirmError.payment_intent);
          // Treat as success since payment already went through
          onSuccess?.(confirmError.payment_intent);
          return;
        }
        
        // Show error and re-enable button for card/validation errors
        if (confirmError.type === 'card_error' || confirmError.type === 'validation_error') {
          setError(confirmError.message);
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
        
        // Release reserved animals since payment failed
        console.log('[PaymentForm] Releasing reserved animals due to payment confirmation failure');
        try {
          await releaseOrderReservation(orderId, {
            headers: {
              'x-idempotency-key': idempotencyKey
            }
          });
          console.log('[PaymentForm] Animals released successfully');
        } catch (releaseError) {
          console.error('[PaymentForm] Failed to release animals:', releaseError);
        }
        
        isProcessingRef.current = false;
        setIsProcessing(false);
        onError?.(confirmError);
      } else {
        console.log('[PaymentForm] confirmPayment.success', {
          paymentIntentId: paymentIntent?.id,
          status: paymentIntent?.status
        });
        
        // Guard: If PI status is already succeeded, go straight to reconciliation
        if (paymentIntent?.status === 'succeeded') {
          console.log('[PaymentForm] PI succeeded - going straight to reconciliation');
        }
        
        // Keep processing state - parent will handle success
        onSuccess?.(paymentIntent);
      }
    } catch (err) {
      console.error('[PaymentForm] Unexpected error:', err);
      setError('An unexpected error occurred');
      
      // Try to release reserved animals if we have an idempotency key
      if (typeof idempotencyKey !== 'undefined') {
        console.log('[PaymentForm] Releasing reserved animals due to unexpected error');
        try {
          await releaseOrderReservation(orderId, {
            headers: {
              'x-idempotency-key': idempotencyKey
            }
          });
          console.log('[PaymentForm] Animals released successfully');
        } catch (releaseError) {
          console.error('[PaymentForm] Failed to release animals:', releaseError);
        }
      }
      
      isProcessingRef.current = false;
      setIsProcessing(false); // Re-enable button on error
      onError?.(err);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          layout: {
            type: 'accordion',
            defaultCollapsed: false,
            radios: false,
            spacedAccordionItems: false
          },
          paymentMethodOrder: ['card'],
          fields: {
            billingDetails: willSupplyBilling ? 'never' : { address: 'auto' },
          },
          ...(billingDetails ? {
            defaultValues: {
              billingDetails: {
                name: billingDetails.name || '',
                email: billingDetails.email || '',
                phone: billingDetails.phone || '',
                address: {
                  line1: billingDetails.address?.line1 || '',
                  line2: billingDetails.address?.line2 || '',
                  city: billingDetails.address?.city || '',
                  state: billingDetails.address?.state || '',
                  postal_code: billingDetails.address?.postal_code || '',
                  country: billingDetails.address?.country || 'US',
                },
              },
            },
          } : {}),
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            // Link off (operator decision 2026-08-31). Its only value is
            // faster REPEAT checkout, which is worthless for a facility
            // whose customers buy a high-value animal once or twice a
            // year — and it carries the Feb-2026 failure shape: a Link
            // account can hold a stale saved address, and paying with a
            // saved Link method can send billing that disagrees with the
            // address typed here (AVS mismatch = declines we can't see).
            // Cowork run 4 found a test-mode Link account already holding
            // a phone number from an EARLIER session, prefilled and
            // pre-checked. 'never' is the same contract applePay and
            // googlePay use above (PaymentWalletOption: 'auto' | 'never').
            link: 'never'
          },
          terms: {
            card: 'never'
          }
        }}
      />
      
      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}
      
      {/* Design System v1 §2.2: Pay is the payment step's ONE primary. */}
      <button
        type="submit"
        className="ff-btn ff-btn--primary ff-btn--block mt-4"
        disabled={isButtonDisabled}
      >
        {isProcessing ? 'Processing Payment...' : `Pay ${formatCurrency(orderTotal)}`}
      </button>
      
      <small className="text-muted mt-2 d-block text-center">
        Items are temporarily reserved during checkout.
      </small>
    </form>
  );
};

export default PaymentForm;